import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, Tab, Tabs, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AnalysisTabBar from '../components/AnalysisTabBar';
import { useAuth } from '../contexts/AuthContext';
import { reportLanguageFromPreference } from '../i18n/inlineTranslations';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';
import PremiumTrialPopup from '../components/PremiumTrialPopup';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

type OutcomeColor = 'error' | 'warning' | 'success' | 'default';
const promiseColor = (outcome: any): OutcomeColor => outcome?.promise === 'not_supported' ? 'error' : outcome?.promise === 'moderate' ? 'warning' : 'success';
const directionColor = (outcome: any): OutcomeColor => outcome?.direction === 'challenging' ? 'error' : outcome?.direction === 'mixed' ? 'warning' : 'success';
const timingColor = (outcome: any): OutcomeColor => !outcome?.activeNow ? 'default' : outcome?.promise === 'not_supported' || outcome?.direction === 'challenging' ? 'error' : outcome?.promise === 'moderate' || outcome?.direction === 'mixed' ? 'warning' : 'success';

const Faladesh: React.FC = () => {
  const { id = '' } = useParams();
  const { i18n, t } = useTranslation();
  const { state: authState } = useAuth();
  const { popup, loading: accessLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [depth, setDepth] = useState<'simple' | 'detailed' | 'professional'>('detailed');
  const language = reportLanguageFromPreference(i18n.resolvedLanguage || i18n.language);

  const generate = useCallback(async () => {
    if (!id || id === 'preview' || !authState.token) return;
    setLoading(true);
    setError('');
    try {
      await checkFeatureAccess({
        featureKey: 'FALADESH',
        apiCall: (trialAccessToken?: string) => axios.post(`${API_BASE_URL}/faladesh/charts/${id}`, {
          language,
          depth,
          systems: ['vedic', 'kp'],
          solutionPreferences: { styles: ['spiritual_and_practical'], noGemstones: true, noPaidRituals: true },
        }, {
          headers: {
            Authorization: `Bearer ${authState.token}`,
            ...(trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : {}),
          },
        }),
        onSuccess: (payload: any) => setReport(payload.data),
      });
    } catch (requestError: any) {
      setError(t('faladeshUi.errorGenerate'));
    } finally {
      setLoading(false);
    }
  }, [authState.token, checkFeatureAccess, depth, id, language, t]);

  useEffect(() => { generate(); }, [generate]);

  const downloadPdf = async () => {
    if (!report?.id || !authState.token) return;
    const response = await fetch(`${API_BASE_URL}/faladesh/${report.id}/pdf`, { headers: { Authorization: `Bearer ${authState.token}` } });
    if (!response.ok) return setError(t('faladeshUi.errorPdf'));
    const blobUrl = URL.createObjectURL(await response.blob());
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = `kundlistar_faladesh_${language}.pdf`;
    anchor.click();
    URL.revokeObjectURL(blobUrl);
  };

  const sectionCards = (sections: any[]) => sections.map((section) => (
    <Card key={section.id} variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" gap={1} mb={1.5}>
          <Typography variant="h6" fontWeight={800}>{section.title}</Typography>
          <Chip size="small" label={section.confidenceLabel} color={section.confidence === 'high' ? 'success' : 'default'} />
        </Stack>
        {section.outcome ? <Stack spacing={0.75} p={1.5} mb={2} borderRadius={2} bgcolor="action.hover"><Stack direction="row" flexWrap="wrap" gap={1}><Chip size="small" color={promiseColor(section.outcome)} label={`${section.outcome.labels.promise}: ${section.outcome.display.promise}`} /><Chip size="small" color={directionColor(section.outcome)} variant="outlined" label={`${section.outcome.labels.direction}: ${section.outcome.display.direction}`} /><Chip size="small" color={timingColor(section.outcome)} variant="outlined" label={`${section.outcome.labels.timing}: ${section.outcome.display.timing}`} /></Stack><Typography variant="body2"><b>{section.outcome.labels.primary}:</b> {section.outcome.display.primaryHouse}</Typography><Typography variant="body2"><b>{section.outcome.labels.lord}:</b> {section.outcome.display.primaryLord}</Typography><Typography variant="body2"><b>{section.outcome.labels.placement}:</b> {section.outcome.display.placementHouse}</Typography><Typography variant="subtitle2">{section.outcome.labels.reasons}</Typography>{section.outcome.reasons.map((reason: string, index: number) => <Typography variant="body2" key={`${section.id}-reason-${index}`}>• {reason}</Typography>)}{section.outcome.recommendations ? <><Typography variant="subtitle2">{section.outcome.recommendations.label}</Typography>{section.outcome.recommendations.items.map((item: string, index: number) => <Typography variant="body2" key={`${section.id}-field-${index}`}>• {item}</Typography>)}</> : null}</Stack> : null}
        {section.paragraphs.map((paragraph: any) => <Typography key={paragraph.role} paragraph sx={{ lineHeight: 1.75 }}>{paragraph.text}</Typography>)}
        {depth === 'professional' ? <Typography variant="caption" color="text.secondary">{section.evidenceFactIds.join(' • ')}</Typography> : null}
      </CardContent>
    </Card>
  ));

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 1.5, md: 3 }, py: 2 }}>
      <AnalysisTabBar chartId={id} active="faladesh" />
      <Card sx={{ mb: 2, background: "linear-gradient(135deg, var(--app-surface-orange, #fff8ed) 0%, var(--app-surface-orange, #f4e4c1) 100%)" }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
            <Box>
              <Chip icon={<AutoAwesomeIcon />} label={t('faladeshUi.deterministic')} color="warning" size="small" />
              <Typography variant="h4" fontWeight={900} mt={1}>{report?.title || t('nav.faladesh')}</Typography>
              <Typography color="text.secondary">{t('faladeshUi.subtitle')}</Typography>
            </Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <FormControl size="small" sx={{ minWidth: 145 }}>
                <InputLabel>{t('faladeshUi.depth')}</InputLabel>
                <Select label={t('faladeshUi.depth')} value={depth} onChange={(event) => setDepth(event.target.value as any)}>
                  <MenuItem value="simple">{t('faladeshUi.simple')}</MenuItem><MenuItem value="detailed">{t('faladeshUi.detailed')}</MenuItem><MenuItem value="professional">{t('faladeshUi.professional')}</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} disabled={!report?.id} onClick={downloadPdf}>{t('faladeshUi.downloadPdf')}</Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      {(loading || accessLoading) ? <Stack alignItems="center" py={8}><CircularProgress /></Stack> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {report ? <>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
          {report.tabs.map((label: string) => <Tab key={label} label={label} />)}
        </Tabs>
        {tab === 0 ? <Stack spacing={1.5}>{report.overview.cards.map((card: any) => <Card key={card.key} variant="outlined"><CardContent><Typography fontWeight={800}>{card.label}</Typography><Typography color="text.secondary">{Array.isArray(card.value) ? card.value.join(' • ') : card.value}</Typography></CardContent></Card>)}</Stack> : null}
        {tab === 1 ? sectionCards(report.systems.vedic) : null}
        {tab === 2 ? sectionCards(report.systems.kp) : null}
        {tab === 3 ? <Card variant="outlined"><CardContent><Typography variant="h6" fontWeight={900}>{report.solutions.title}</Typography><Typography paragraph>{report.solutions.primary.text}</Typography>{report.solutions.supporting.map((item: any) => <Typography paragraph key={item.text}>{item.text}</Typography>)}{report.solutions.optionalSpiritual ? <Typography paragraph>{report.solutions.optionalSpiritual.text}</Typography> : null}<Alert severity="info">{report.solutions.avoid}</Alert></CardContent></Card> : null}
        {tab === 4 ? <Stack spacing={1}>{report.evidence.map((fact: any, index: number) => <Card key={`${fact.id}-${index}`} variant="outlined"><CardContent><Typography fontWeight={800}>{fact.subject}</Typography><Typography variant="body2">{fact.value}</Typography><Typography variant="caption" color="text.secondary">{fact.id}</Typography></CardContent></Card>)}</Stack> : null}
        <Typography variant="caption" color="text.secondary" display="block" mt={3}>{report.disclaimer}</Typography>
      </> : null}
      <PremiumTrialPopup open={popup.open} featureName={popup.featureName} used={popup.used} total={popup.total} remaining={popup.remaining} loading={accessLoading} onCancel={closePopup} onContinue={popup.nextAction} />
    </Box>
  );
};

export default Faladesh;
